import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  QrCode, 
  FileText, 
  BarChart2, 
  Package, 
  CheckCircle, 
  Menu, 
  X
} from 'lucide-react';

const HomePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleDashboardClick = () => {
    navigate('/dashboard', { replace: true });
  };

  const features = [
    {
      icon: <FileText size={24} className="text-blue-500" />,
      title: 'Custom Feedback Forms',
      description: 'Create fully customizable forms with multiple question types including text, multiple choice, and ratings.'
    },
    {
      icon: <QrCode size={24} className="text-blue-500" />,
      title: 'QR Code Generation',
      description: 'Instantly generate unique QR codes for each form that customers can scan with any smartphone.'
    },
    {
      icon: <BarChart2 size={24} className="text-blue-500" />,
      title: 'Detailed Analytics',
      description: 'View comprehensive analytics and insights from all your feedback responses in real-time.'
    },
    {
      icon: <Package size={24} className="text-blue-500" />,
      title: 'Physical QR Stickers(soon)',
      description: 'Order professionally printed QR code stickers to place in your business locations.'
    },
  ];

  const pricing = {
    starter: {
      name: 'Starter',
      price: '$9',
      duration: '/month',
      features: [
        '1 active forms',
        'Basic analytics',
        '100 responses per month'
      ],
      cta: 'Start free trial',
      mostPopular: false,
    },
    professional: {
      name: 'Professional',
      price: '$29',
      duration: '/month',
      features: [
        '15 forms',
        'Unlimited responses',
        'Email notifications'
      ],
      cta: 'Start free trial',
      mostPopular: true,
    },
    enterprise: {
      name: 'Enterprise',
      price: '$199',
      duration: '/month',
      features: [
        '100 forms',
        'Unlimited responses',
        'Email notifications'
      ],
      cta: 'Start free trial',
      mostPopular: false,
    },
  };

  return (
    <div className="bg-white">
      <header className="relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex justify-between items-center border-b-2 border-gray-100 py-6 md:justify-start md:space-x-10">
            <div className="flex justify-start lg:w-0 lg:flex-1">
              <Link to="/" className="flex items-center">
                <QrCode size={32} className="text-blue-600" />
                <span className="ml-2 text-xl font-bold text-gray-900">QR Feedback</span>
              </Link>
            </div>
            
            <div className="-mr-2 -my-2 md:hidden">
              <button
                type="button"
                className="bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                onClick={() => setMobileMenuOpen(true)}
              >
                <span className="sr-only">Open menu</span>
                <Menu size={24} aria-hidden="true" />
              </button>
            </div>
            
            <nav className="hidden md:flex space-x-10">
              <Link to="/pricing" className="text-base font-medium text-gray-500 hover:text-gray-900">
                Pricing
              </Link>
              <Link to="/about" className="text-base font-medium text-gray-500 hover:text-gray-900">
                About
              </Link>
            </nav>
            
            <div className="hidden md:flex items-center justify-end md:flex-1 lg:w-0">
              {user ? (
                <button
                  onClick={handleDashboardClick}
                  className="ml-8 whitespace-nowrap inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  Dashboard
                </button>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="whitespace-nowrap text-base font-medium text-gray-500 hover:text-gray-900"
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/signup"
                    className="ml-8 whitespace-nowrap inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    Sign up
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="absolute top-0 inset-x-0 p-2 transition transform origin-top-right md:hidden z-10">
            <div className="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 bg-white divide-y-2 divide-gray-50">
              <div className="pt-5 pb-6 px-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <QrCode size={32} className="text-blue-600" />
                    <span className="ml-2 text-xl font-bold text-gray-900">QR Feedback</span>
                  </div>
                  <div className="-mr-2">
                    <button
                      type="button"
                      className="bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="sr-only">Close menu</span>
                      <X size={24} aria-hidden="true" />
                    </button>
                  </div>
                </div>
                <div className="mt-6">
                  <nav className="grid gap-y-8">
                    <Link
                      to="/pricing"
                      className="-m-3 p-3 flex items-center rounded-md hover:bg-gray-50"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="ml-3 text-base font-medium text-gray-900">
                        Pricing
                      </span>
                    </Link>
                    <Link
                      to="/about"
                      className="-m-3 p-3 flex items-center rounded-md hover:bg-gray-50"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className="ml-3 text-base font-medium text-gray-900">
                        About
                      </span>
                    </Link>
                  </nav>
                </div>
              </div>
              <div className="py-6 px-5 space-y-6">
                <div>
                  {user ? (
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        handleDashboardClick();
                      }}
                      className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700"
                    >
                      Dashboard
                    </button>
                  ) : (
                    <>
                      <Link
                        to="/signup"
                        className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Sign up
                      </Link>
                      <p className="mt-6 text-center text-base font-medium text-gray-500">
                        Existing customer?{' '}
                        <Link 
                          to="/login" 
                          className="text-blue-600 hover:text-blue-500"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          Sign in
                        </Link>
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </header>

      <main>
        <div className="relative">
          <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gray-100" />
          <div className="max-w-7xl mx-auto sm:px-6 lg:px-8">
            <div className="relative shadow-xl sm:rounded-2xl sm:overflow-hidden">
              <div className="absolute inset-0">
                <img
                  className="h-full w-full object-cover"
                  src="https://images.pexels.com/photos/3184296/pexels-photo-3184296.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
                  alt="People working on laptops"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-blue-800 to-blue-700 mix-blend-multiply" />
              </div>
              <div className="relative px-4 py-16 sm:px-6 sm:py-24 lg:py-32 lg:px-8">
                <h1 className="text-center text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                  <span className="block text-white">Collect customer feedback</span>
                  <span className="block text-blue-200">with QR codes</span>
                </h1>
                <p className="mt-6 max-w-lg mx-auto text-center text-xl text-blue-100 sm:max-w-3xl">
                  Create custom feedback forms, generate QR codes, and get valuable insights from your customers in real-time.
                </p>
                <div className="mt-10 max-w-sm mx-auto sm:max-w-none sm:flex sm:justify-center">
                  <div className="space-y-4 sm:space-y-0 sm:mx-auto sm:inline-grid sm:grid-cols-2 sm:gap-5">
                    <Link
                      to="/signup"
                      className="flex items-center justify-center px-4 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-blue-700 bg-white hover:bg-blue-50 sm:px-8"
                    >
                      Get started
                    </Link>
                    <Link
                      to="/about"
                      className="flex items-center justify-center px-4 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-500 bg-opacity-60 hover:bg-opacity-70 sm:px-8"
                    >
                      Learn more
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="lg:text-center">
              <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">Features</h2>
              <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
                A better way to collect feedback
              </p>
              <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
                Create and customize forms, generate QR codes, and analyze responses to improve your business.
              </p>
            </div>

            <div className="mt-10">
              <dl className="space-y-10 md:space-y-0 md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-10">
                {features.map((feature, index) => (
                  <div key={index} className="relative">
                    <dt>
                      <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-blue-50 text-blue-500">
                        {feature.icon}
                      </div>
                      <p className="ml-16 text-lg leading-6 font-medium text-gray-900">{feature.title}</p>
                    </dt>
                    <dd className="mt-2 ml-16 text-base text-gray-500">{feature.description}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
                How QR Feedback works
              </h2>
              <p className="mt-4 text-lg text-gray-500">
                Get started in minutes with our simple step-by-step process
              </p>
            </div>
            <div className="mt-12">
              <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
                <div className="bg-white overflow-hidden shadow rounded-lg">
                  <div className="px-4 py-5 sm:p-6">
                    <div className="flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 text-blue-600 mx-auto">
                      <span className="text-xl font-bold">1</span>
                    </div>
                    <h3 className="mt-6 text-center text-lg font-medium text-gray-900">Create Your Form</h3>
                    <p className="mt-2 text-sm text-gray-500 text-center">
                      Design custom feedback forms using our intuitive form builder with various question types.
                    </p>
                  </div>
                </div>

                <div className="bg-white overflow-hidden shadow rounded-lg">
                  <div className="px-4 py-5 sm:p-6">
                    <div className="flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 text-blue-600 mx-auto">
                      <span className="text-xl font-bold">2</span>
                    </div>
                    <h3 className="mt-6 text-center text-lg font-medium text-gray-900">Generate QR Code</h3>
                    <p className="mt-2 text-sm text-gray-500 text-center">
                      Each form gets a unique QR code that you can download and print for your business locations.
                    </p>
                  </div>
                </div>

                <div className="bg-white overflow-hidden shadow rounded-lg">
                  <div className="px-4 py-5 sm:p-6">
                    <div className="flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 text-blue-600 mx-auto">
                      <span className="text-xl font-bold">3</span>
                    </div>
                    <h3 className="mt-6 text-center text-lg font-medium text-gray-900">Collect & Analyze</h3>
                    <p className="mt-2 text-sm text-gray-500 text-center">
                      Customers scan the QR code to submit feedback, and you gain valuable insights from the responses.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="sm:flex sm:flex-col sm:align-center">
              <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl text-center">
                Simple, transparent pricing
              </h2>
              <p className="mt-5 text-xl text-gray-500 sm:text-center">
                Start with a 14-day free trial. No credit card required.
              </p>
            </div>
            <div className="mt-12 space-y-4 sm:mt-16 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-6 lg:max-w-4xl lg:mx-auto xl:max-w-none xl:mx-0 xl:grid-cols-3">
              {Object.values(pricing).map((tier) => (
                <div key={tier.name} className={`rounded-lg shadow-sm divide-y divide-gray-200 ${
                  tier.mostPopular ? 'border-2 border-blue-500' : 'border border-gray-200'
                }`}>
                  {tier.mostPopular && (
                    <div className="bg-blue-500 rounded-t-lg py-1.5">
                      <p className="text-xs font-medium text-white text-center">Most popular</p>
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="text-lg font-medium text-gray-900">{tier.name}</h3>
                    <p className="mt-4 flex items-baseline text-gray-900">
                      <span className="text-4xl font-extrabold tracking-tight">{tier.price}</span>
                      <span className="ml-1 text-xl font-semibold">{tier.duration}</span>
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      All the basics to get started collecting feedback
                    </p>
                    <Link
                      to="/signup"
                      className={`mt-6 block w-full py-2 px-4 border border-transparent rounded-md shadow text-center text-sm font-medium ${
                        tier.mostPopular
                          ? 'text-white bg-blue-600 hover:bg-blue-700'
                          : 'text-blue-600 bg-white border-blue-600 hover:bg-blue-50'
                      }`}
                    >
                      {tier.cta}
                    </Link>
                  </div>
                  <div className="pt-6 pb-8 px-6">
                    <h4 className="text-sm font-medium text-gray-900">What's included</h4>
                    <ul className="mt-4 space-y-3">
                      {tier.features.map((feature, index) => (
                        <li key={index} className="flex">
                          <CheckCircle size={20} className="flex-shrink-0 text-green-500" />
                          <span className="ml-3 text-sm text-gray-500">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-blue-700">
          <div className="max-w-2xl mx-auto text-center py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
              <span className="block">Ready to improve your customer feedback?</span>
            </h2>
            <p className="mt-4 text-lg leading-6 text-blue-200">
              Start your 14-day free trial today. No credit card required.
            </p>
            <Link
              to="/signup"
              className="mt-8 w-full inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-blue-600 bg-white hover:bg-blue-50 sm:w-auto"
            >
              Get started for free
            </Link>
          </div>
        </div>
      </main>

      <footer className="bg-white">
        <div className="max-w-7xl mx-auto py-12 px-4 overflow-hidden sm:px-6 lg:px-8">
          <div className="flex flex-col items-center">
            <Link to="/" className="flex items-center">
              <QrCode size={24} className="text-blue-600" />
              <span className="ml-2 text-lg font-bold text-gray-900">QR Feedback</span>
            </Link>
            <nav className="mt-8 flex flex-wrap justify-center" aria-label="Footer">
              <div className="px-5 py-2">
                <Link to="/pricing" className="text-base text-gray-500 hover:text-gray-900">
                  Pricing
                </Link>
              </div>
              <div className="px-5 py-2">
                <Link to="/about" className="text-base text-gray-500 hover:text-gray-900">
                  About
                </Link>
              </div>
              <div className="px-5 py-2">
                <Link to="/privacy" className="text-base text-gray-500 hover:text-gray-900">
                  Privacy
                </Link>
              </div>
              <div className="px-5 py-2">
                <Link to="/terms" className="text-base text-gray-500 hover:text-gray-900">
                  Terms
                </Link>
              </div>
            </nav>
            <div className="mt-4 flex flex-col items-center space-y-2">
              <p className="text-center text-base text-gray-400">
                &copy; 2025 QR Feedback, Inc. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;